import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import Modal from '@lib/components/Modal/Modal';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { PlanState } from '@lib/enums/plan';
import { IconName, ModalIconColor } from '@lib/components/Modal/enums';
import getFormFields from '@lib/components/UpgradePlanModal/utils/getFormField';
import {
  GetUserAccessByCompany,
  getAllFeaturesUnlocked,
  getFeatureSelectOptions,
} from '@lib/components/UpgradePlanModal/utils/upgradePlanFeatureOptions';
import Form from '@lib/components/ReactHookForm/FormContainer';
import { materialSymbolsOutlined } from '@lib/enums/common';
import { Values } from '@lib/interfaces/form';
import {
  CompanyPlanUpgradeFeatureEnum,
  useCompanyPlanChangeRequestMutation,
} from '@lib/graphql-common';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import getAppUrlWithDomain from '@lib/utils/getAppUrlWithDomain';
import { SYSTEM_APP_URLS } from '@lib/constants/urls';
import Trans from '@lib/components/Trans/Trans';
import UpgradePlanFieldNames from '@lib/enums/fieldNames/upgradePlanFieldNames';

const { VITE_SYSTEM_PLATFORM_URL: SYSTEM_PLATFORM } = import.meta.env;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  planState?: PlanState;
  companyId: string;
  contactEmail?: string;
  preselectedFeatures?: CompanyPlanUpgradeFeatureEnum[];
  getUserAccessByCompany?: GetUserAccessByCompany;
  usersLimit?: number | null;
}

const modalData = {
  [PlanState.Attention]: {
    title: 'upgrade-plan-modal-title-attention',
    description: 'upgrade-plan-modal-text-attention',
    icon: IconName.ErrorRed,
    iconColor: ModalIconColor.Red,
  },
  [PlanState.Warning]: {
    title: 'upgrade-plan-modal-title-warning',
    description: 'upgrade-plan-modal-text-warning',
    icon: IconName.WarningYellow,
    iconColor: ModalIconColor.Yellow,
  },
  [PlanState.Locked]: {
    title: 'upgrade-plan-modal-title-locked',
    description: 'upgrade-plan-modal-text-locked',
    icon: IconName.Lock,
    iconColor: ModalIconColor.Blue,
  },
  [PlanState.None]: {
    title: 'title',
    description: 'text',
    icon: IconName.ErrorRed,
    iconColor: ModalIconColor.Blue,
  },
};

const contactUsModalData = {
  title: 'contact-our-team',
  description: 'contact-our-team-modal-text',
  icon: IconName.Mail,
  iconColor: ModalIconColor.Blue,
};

export default function UpgradePlanModal(props: Props) {
  const {
    isOpen,
    onClose,
    planState = PlanState.None,
    companyId,
    contactEmail,
    preselectedFeatures,
    getUserAccessByCompany,
    usersLimit,
  } = props;
  const { t } = useTranslation();
  const onConfirm = () => {
    return undefined;
  };
  // "Locked" is the feature-unlock flow; once every requestable feature is
  // already unlocked there's nothing left to upgrade, so the CTA switches to
  // a generic "Contact our team" request instead.
  const isContactUsMode =
    planState === PlanState.Locked &&
    getAllFeaturesUnlocked({ getUserAccessByCompany });
  const { title, description, icon, iconColor } = isContactUsMode
    ? contactUsModalData
    : modalData[planState];
  const isModalOpen = planState === PlanState.None ? false : isOpen;
  const fields = useMemo(
    () =>
      getFormFields({ t, getUserAccessByCompany, isContactUsMode, usersLimit }),
    [t, getUserAccessByCompany, isContactUsMode, usersLimit],
  );
  const preselectedKey = (preselectedFeatures ?? []).join(',');
  const defaultValues = useMemo(() => {
    const featureOptions = preselectedFeatures?.length
      ? getFeatureSelectOptions({
          t,
          getUserAccessByCompany,
          isContactUsMode,
        }).filter(({ value }) => preselectedFeatures.includes(value))
      : [];
    return {
      ...(contactEmail
        ? { [UpgradePlanFieldNames.ContactEmail]: contactEmail }
        : {}),
      ...(featureOptions.length
        ? { [UpgradePlanFieldNames.Features]: featureOptions }
        : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedKey, contactEmail, t, isContactUsMode]);
  const [companyPlanChangeRequest, { loading }] =
    useCompanyPlanChangeRequestMutation({
      onCompleted: () => {
        toast({ content: t('upgrade-plan-success') });
        onClose();
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });
  const onSubmit = (values: Values) => {
    const baseMessage = _get(values, UpgradePlanFieldNames.Message) as string;
    const submittedContactEmail = _get(
      values,
      UpgradePlanFieldNames.ContactEmail,
    ) as string;
    const desiredUserLimit = _get(
      values,
      UpgradePlanFieldNames.DesiredUserLimit,
    ) as string;
    const features = (
      _get(values, UpgradePlanFieldNames.Features, []) as Array<{
        value: CompanyPlanUpgradeFeatureEnum;
      }>
    ).map(({ value }) => value);
    const desiredUsersLimit =
      features.includes(CompanyPlanUpgradeFeatureEnum.UserLimit) &&
      desiredUserLimit
        ? Number(desiredUserLimit)
        : undefined;
    const companyProfileLink = getAppUrlWithDomain(
      SYSTEM_APP_URLS.dashboard.companies.view.getDynamicPath({
        pathParts: { id: companyId },
      }),
      SYSTEM_PLATFORM,
    );
    companyPlanChangeRequest({
      variables: {
        input: {
          params: {
            message: baseMessage,
            companyProfileLink,
            contactEmail: submittedContactEmail,
            ...(desiredUsersLimit ? { desiredUsersLimit } : {}),
            ...(features.length ? { features } : {}),
          },
        },
      },
    });
  };
  const actions = () => (
    <ActionsRow
      actions={[
        <Button
          onClick={onClose}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
        />,
        <Button
          type="submit"
          disabled={loading}
          isLoading={loading}
          onClick={onConfirm}
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t('send-request')}
          leftIcon="send"
          leftIconClassName={materialSymbolsOutlined}
          fullWidth
        />,
      ]}
    />
  );
  return (
    <Modal
      description={<Trans i18nKey={description} />}
      icon={icon}
      iconColor={iconColor}
      isOpen={isModalOpen}
      onClose={onClose}
      title={t(title)}
    >
      <Form
        formId="upgrade-plan"
        fields={fields}
        defaultValues={defaultValues}
        actions={actions}
        loading={loading}
        onSubmit={onSubmit}
      />
    </Modal>
  );
}
